import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useContext,
} from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useErrorService } from '@abroad/components'
import { OrgEcoListingTable, useQuery } from '../components/common'
import { EntityArchiveAction } from '../components/company'
import API from '../utils/API'
import { ListingFiltersContext } from '../utils/contexts'

export const EcoSystemListing = () => {
  const hasMore = useRef(false)
  const [isLoading, setIsLoading] = useState(true)
  const [ecoSystemData, setEcoSystemData] = useState([])
  const [reqData, setReqData] = useState(null)
  const Error = useErrorService()
  const query = useQuery()
  const search = query.get('search')
  const { setFilters } = useContext(ListingFiltersContext)
  const location = useLocation()
  const isArchived = location?.pathname === '/admin/networks/archive'

  const getEcoSystemList = useCallback(async () => {
    setIsLoading(true)
    try {
      const { data } = await API.AdminAPI.companyList.getEcoSystemList(reqData)
      if (data.length === 0 || data.length < 25) {
        hasMore.current = false
      } else {
        hasMore.current = true
      }
      if (reqData.page > 1) {
        setEcoSystemData((prev) => [...prev, ...data])
      } else {
        setEcoSystemData(data)
      }
      setFilters((prev) => ({
        ...prev,
        sort: reqData?.filter?.sort,
      }))
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
      Error.showError(e)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqData])

  useEffect(() => {
    if (reqData) {
      getEcoSystemList(reqData)
    }
  }, [getEcoSystemList, reqData])

  // Archived or restored rows leave the current list, as the server now filters them out
  const removeNetwork = useCallback((sourceNetwork) => {
    setEcoSystemData((prev) =>
      prev.filter((network) => network.affiliation !== sourceNetwork),
    )
  }, [])

  const columns = React.useMemo(
    () => [
      {
        id: 'Network',
        Header: 'Network',
        width: '40%',
        accessor: (properties) => properties.affiliation,
        Cell: ({ cell }) => (
          <span className='word-breakall'>
            <Link
              to={`/admin/network/${cell.value}/users`}
              className='text-dark-900'>
              {cell.value}
            </Link>
          </span>
        ),
      },
      {
        id: 'Unique User Assessments',
        Header: 'Unique User Assessments',
        width: '40%',
        disableSortBy: true,
        dataProps: {
          className: 'text-center',
        },
        headerProps: {
          className: 'text-center',
          wrapperClassName: 'd-flex-unset',
        },
        accessor: (properties) => properties.surveyCount,
      },
      {
        id: 'actions',
        Header: 'Action',
        width: '20%',
        disableSortBy: true,
        accessor: (properties) => properties,
        Cell: ({ cell }) => (
          <EntityArchiveAction
            type='network'
            name={cell.value.affiliation}
            entityId={cell.value._id}
            isArchived={cell.value.isArchived}
            onChanged={removeNetwork}
          />
        ),
      },
    ],
    [removeNetwork],
  )

  const fetchData = useCallback(
    ({ search, sortBy, page, limit }) => {
      if (page === 1) {
        setEcoSystemData([])
      }
      const req = {
        limit,
        page: page,
        filter: {
          ...(search && {
            search: search,
          }),
          isArchived,
          sort: !sortBy[0].desc ? 'ASC' : 'DESC',
        },
      }
      setReqData(req)
    },
    [isArchived],
  )

  return (
    <OrgEcoListingTable
      key={isArchived ? 'archive' : 'active'}
      columns={columns}
      data={ecoSystemData}
      label='Networks'
      sortById='Network'
      basePath='/admin/networks'
      search={search}
      fetchData={fetchData}
      isLoading={isLoading}
      hasMore={hasMore}
      type='network'
    />
  )
}
